import React from 'react';
import styled from 'styled-components';
import i18n from 'i18next';
import { HYPHEN } from 'modules/core/constants';
import { CANONICAL_COLOR_HEX, CanonicalColor } from 'modules/core/constants/vehicleColors.const';

const Row = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 20px;
`;

const Swatch = styled.span<{ $hex: string; $size?: number }>`
  display: inline-block;
  width: ${({ $size }) => $size ?? 12}px;
  height: ${({ $size }) => $size ?? 12}px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $hex }) => $hex};
  border: 1px solid rgba(0, 0, 0, 0.18);
  box-sizing: border-box;
`;

const Label = styled.span`
  font-size: inherit;
  line-height: 1.2;
`;

const FloatingSwatch = styled(Swatch)`
  box-shadow: 0 0 0 2px #ffffff, 0 1px 2px rgba(0, 0, 0, 0.2);
`;

export interface IColorDotProps {
  canonical?: CanonicalColor | string | null;
  label?: string | null;
}

export interface IColorBadgeProps extends IColorDotProps {
  size?: number;
  className?: string;
}

const resolveCanon = (canonical?: CanonicalColor | string | null): CanonicalColor | null =>
  canonical && CANONICAL_COLOR_HEX[canonical as CanonicalColor] ? (canonical as CanonicalColor) : null;

/** Standalone swatch used as an overlay badge (no text). Renders nothing if no color data. */
export const ColorBadge: React.FC<IColorBadgeProps> = ({ canonical, label, size = 14, className }) => {
  const trimmed = label?.trim();
  const canon = resolveCanon(canonical);

  if (!trimmed && !canon) return null;

  const hex = canon ? CANONICAL_COLOR_HEX[canon] : CANONICAL_COLOR_HEX.other;
  const title = trimmed || (canon ? i18n.t(`components.vehicleFilters.colors.${canon}`) : undefined);

  return <FloatingSwatch className={className} $hex={hex} $size={size} title={title} aria-label={title} />;
};

export const ColorDot: React.FC<IColorDotProps & { gap?: number }> = ({ canonical, label, gap = 8 }) => {
  const trimmed = label?.trim();
  const canon = resolveCanon(canonical);
  const hasAny = Boolean(trimmed) || canon != null;

  if (!hasAny) {
    // Match other columns' empty rendering (plain HYPHEN text, no wrapping element)
    // so vertical alignment lines up with the rest of the row.
    return <>{HYPHEN}</>;
  }

  const fallbackLabel = canon != null ? i18n.t(`components.vehicleFilters.colors.${canon}`) : undefined;
  const text = trimmed || fallbackLabel || HYPHEN;
  const ariaLabel = trimmed || fallbackLabel || text;

  const hex = canon != null ? CANONICAL_COLOR_HEX[canon] : null;

  return (
    <Row title={ariaLabel} aria-label={ariaLabel} style={{ gap: gap }}>
      {hex && <Swatch $hex={hex} />}
      <Label>{text}</Label>
    </Row>
  );
};
